/* Conversations passées d'un élève, consultées depuis le compte de son
   responsable. Même forme que la page enseignante `conversations.tsx`, mais la
   liste vient de `parentOrTutor.student.chatThreads`, qui porte le contrôle du
   lien parent↔enfant : `listThreads` exclut ces fils par construction, et un
   parent à plusieurs enfants ne doit jamais voir ceux de l'un sous l'autre. */

import { useEffect, useState } from 'react';
import { useParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import ThreadRow from '../components/thread-row';
import { Alert } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '../lib/api';
import type { ChatThread } from '../lib/chat-api';

export default function StudentConversations() {
  const { studentId } = useParams<{ studentId: string }>();
  const [threads, setThreads] = useState<ChatThread[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setThreads(null);
    setError(null);
    api.parentOrTutor.student.chatThreads
      .query({ studentId })
      .then((t) => !cancelled && setThreads(t as ChatThread[]))
      .catch(() => !cancelled && setError("Les conversations n'ont pas pu être chargées."));
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <HeaderContent width="md" showBurger backTo={`/tutor/enfant/${studentId}`} backLabel="Fiche">
        <h1 className={HEADER_TITLE_CLASS}>Tes conversations avec Naïa</h1>
      </HeaderContent>

      <div className="max-w-[880px] mx-auto px-[max(16px,env(safe-area-inset-left))] py-4 pb-16 flex-1 w-full">
        {error && <Alert>{error}</Alert>}

        {threads === null && !error && (
          <div className="flex flex-col gap-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[62px] rounded-card" />
            ))}
          </div>
        )}

        {threads?.length === 0 && (
          <div className="text-center py-16 px-4">
            <p className="font-extrabold text-foreground text-[14px]">Aucune conversation pour l'instant</p>
            <p className="text-[13px] text-muted-foreground mt-1">
              Les discussions avec Naïa apparaîtront ici.
            </p>
          </div>
        )}

        {threads && threads.length > 0 && (
          <div className="rounded-card border border-border overflow-hidden">
            {threads.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                to={`/tutor/enfant/${studentId}/chat?thread=${t.id}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
